import { fetchApi } from "../helpers/reactQueryApi";
import { convertPayload } from "../api/unreads";
import store from "../store.js";
import { DeviceData } from "../@types/devices";
import {
  disableBodyScrolling,
  restoreBodyScrolling,
} from "../layout/togglebar";

// The chat the app last asked for, until the chat page has taken it. Only ever
// set while that page is away: with the page up the dispatch below arrives on
// its own, and a value kept beyond that would open a stale chat the next time
// the page comes up (tixxt/core#444).
let requestedChatId: string | null = null;
let chatPageIsUp = false;

export function takeRequestedChat(): string | null {
  const id = requestedChatId;
  requestedChatId = null;
  return id;
}

export function setChatPageIsUp(isUp: boolean) {
  chatPageIsUp = isUp;
}

export default class Bridge {
  static isIOS() {
    return /iOS/.test(navigator.userAgent);
  }

  static isAndroid() {
    return /Android/.test(navigator.userAgent);
  }

  static postMessage(message: object) {
    if (Bridge.isIOS()) {
      window.webkit?.messageHandlers.nativeApp.postMessage(message);
    }

    if (Bridge.isAndroid()) {
      window["nativeApp"]?.postMessage(JSON.stringify(message));
    }
  }

  static setNewItemButton(newUrl: string) {
    Bridge.postMessage({ setNewItemButton: newUrl });
  }

  static navigateInApp(url: string) {
    Bridge.postMessage({ navigate: url });
  }

  /** Called by native app via window.bridge */
  static toggleUserbar() {
    const userbar = document.getElementById("userbar-root");

    if (userbar?.classList.contains("userbar--visible")) {
      userbar?.classList.remove("userbar--visible");
      restoreBodyScrolling("userbar");
    } else {
      userbar?.classList.add("userbar--visible");
      disableBodyScrolling("userbar");
    }
  }

  /** Called by native app via window.bridge */
  static toggleChat() {
    const chat = document.getElementById("chatbar-root");

    if (chat?.classList.contains("chatbar--visible")) {
      chat?.classList.remove("chatbar--visible");
    } else {
      chat?.classList.add("chatbar--visible");
    }
  }

  /** Called by native app via window.bridge */
  static toggleSearchbar() {
    const searchbar = document.getElementById("app__header");
    const searchInput: HTMLInputElement = document.getElementsByClassName(
      "search-bar__input",
    )[0] as HTMLInputElement;

    if (searchbar?.classList.contains("header--visible")) {
      searchbar?.classList.remove("header--visible");
      searchInput.blur();
    } else {
      searchbar?.classList.add("header--visible");
      searchInput?.focus();
    }
  }

  /** Called by native app via window.bridge */
  static registerDevice(data: DeviceData) {
    fetchApi("/api/v1/devices", {
      method: "POST",
      body: data,
    });
  }

  static askToRegisterDevice() {
    Bridge.postMessage({ registerDevice: "registerDevice" });
  }

  static async removeDevice(deviceUniqId: string) {
    const result = await fetchApi(`/api/v1/devices/${deviceUniqId}`, {
      method: "DELETE",
    });

    Bridge.postMessage({ deletedDevice: result });
  }

  /** Called by native app via window.bridge */
  static showChat(id: string) {
    // The app calls this on a timer after a push notification, which can land
    // before the chat page has mounted and registered its reducer. The request
    // would then be dropped without a trace, and the user stares at an empty
    // view (tixxt/core#444). So it is remembered while the page is away, and
    // the page picks it up when it comes up.
    if (!chatPageIsUp) requestedChatId = id;

    store.dispatch({
      type: "chat/NAVIGATE",
      payload: { route: "showChat", params: { chatId: id } },
    });
  }

  /** Called by native app via window.bridge */
  static async loadNetworkConfig() {
    const network = await fetchApi("/api/v1/network");

    const pages = await fetchApi("/pages");

    Bridge.postMessage({
      setNetworkConfig: network,
      setPages: pages,
    });
  }

  /** Called by native app via window.bridge */
  static async loadUnreads() {
    const data = await fetchApi("/unreads/features");
    const unreads = convertPayload(data);

    Bridge.postMessage({ unreads });
  }

  static getDeviceUniqueId(
    setDeviceUniqueId: (props: { id: string; name: string }) => void,
  ) {
    Bridge.postMessage({
      getDeviceUniqueId: "setDeviceUniqueId",
    });

    Bridge["setDeviceUniqueId"] = setDeviceUniqueId;
  }

  static setPageTitle(title: string) {
    setTimeout(() => {
      Bridge.postMessage({
        pageTitle: title,
      });
    }, 200);
  }

  static goBack() {
    Bridge.postMessage({
      goBack: "goBack",
    });
  }

  static setMemberIsLoggedIn(isLoggedIn: boolean) {
    Bridge.postMessage({
      isLoggedIn,
    });
  }

  static setMemberId(membershipId: string) {
    Bridge.postMessage({
      membershipId,
    });
  }

  static setMemberCanChat(canChat: boolean) {
    Bridge.postMessage({
      canChat,
    });
  }

  static configureLayout() {
    document.body.classList.add("tixxt--in-app");
    // tells newer apps that the page keeps clear of their bars (mobile_app.css),
    // so they may lay them over it (turbo-apps#224)
    document.documentElement.dataset.appInsets = "supported";

    // Only the heading: the static title may also hold a page action (#282),
    // whose label must not end up in the app's title bar
    const pageTitle = document.querySelector("#static-page-title h1");

    if (pageTitle?.textContent) {
      Bridge.setPageTitle(pageTitle.textContent);
    }

    const title = document.querySelector("title");
    if (title) {
      title?.parentNode?.removeChild(title);
    }
  }

  /** Called by native app via window.bridge */
  static async getAuthToken(key: string, options: { url: string }) {
    const token = await fetchApi("/api/v1/get-web-token");

    Bridge.postMessage({
      [key]: { authToken: token, options },
    });
  }

  static alert(
    title: string,
    message: string,
    callbackName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback?: (...args: any[]) => void,
    defaultValue?: string,
  ) {
    Bridge.postMessage({
      alert: { title, message, defaultValue, callbackName },
    });

    if (callback) {
      Bridge[callbackName] = callback;
    }
  }

  static prompt(
    message: string,
    defaultValue?: string,
    callback?: (userInput?: string | null) => void,
  ) {
    if (window.isApp) {
      Bridge.alert("", message, "promptCallback", callback, defaultValue);
    } else {
      const userInput = prompt(message, defaultValue);
      if (callback) callback(userInput);
    }
  }

  /** onCancel reaches the browser dialog only — the native app calls back
   * on a confirmed dialog and stays silent on a dismissed one */
  static confirm(message: string, callback: () => void, onCancel?: () => void) {
    if (window.isApp) {
      Bridge.alert(message, "", "confirmCallback", callback, undefined);
    } else {
      if (confirm(message)) callback();
      else onCancel?.();
    }
  }

  /** Called by native app via window.bridge */
  static onTabChanged(tabName: string) {
    const event = new CustomEvent("nativeTabChanged", { detail: { tabName } });
    document.dispatchEvent(event);
  }

  static setPullToRefreshEnabled(enabled: boolean) {
    Bridge.postMessage({
      setPullToRefreshEnabled: enabled,
    });
  }
}
